import React, { useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import PropTypes from 'prop-types';
import styled from 'styled-components';
import Carousel, {
  CarouselCard,
  getCardSizes,
} from '~/shared-components/Carousel';
import { PostTypeStory } from '~/post/constants';
import {
  fontFamily,
  fontSizeMini,
  fontWeight,
  fontWeightMedium,
} from '@bufferapp/components/style/font';
import { red } from '@bufferapp/components/style/color';
import { useOldUploaderCallback } from '~/UploadZone/utils/useOldUplodaderCallback';
import { useStoryGroupNotifiers } from '~/story-group-composer/useStoryGroupNotifiers';
import { validateFile } from '~/uploads/lib/validation/validateFilesForUpload';
import { UploaderRestrictions } from '~/uploads/values/UploaderRestrictions';
import { SEGMENT_NAMES, UploadTypes } from '../../../constants';
import { FileUploadConfig } from '../../../uploads/constants';
import HeaderBar from '../HeaderBar';
import AddNote from '../AddNote';
import CarouselCards from '../Carousel/CarouselCards';
import CardItem from '../Carousel/CardItem';
import AddStoryFooter from '../AddStoryFooter';
import {
  IntegrationsBar,
  useIntegrationServices,
  IntegrationsWrapper,
} from '../../../integrations-bar';
import { actions } from '../../reducer';

const ADD_STORY = 'addStory';
const ADD_NOTE = 'addNote';

const WrapperStyle = styled.div`
  width: 728px;
  height: 100%;
  background-color: white;
  top: 0;
  right: 0;
  border-radius: 3px;
  padding: 16px;
`;

const ErrorMessage = styled.div`
  display: block;
  background-color: rgba(243, 175, 185, 0.25);
  color: ${red};
  margin: 8px 0;
  padding: 8px;
  font-size: ${fontSizeMini};
  font-weight: ${fontWeight};
`;

const CarouselCardCongrats = styled.span`
  color: inherit;
  font-family: ${fontFamily};
  font-size: ${fontSizeMini};
  font-weight: ${(props) => props.weight};
  line-height: inherit;
  margin: 8px;
  text-align: center;
`;

const ErrorHandler = ({ errorMessages }) => {
  if (!errorMessages) return null;
  return (
    errorMessages &&
    errorMessages.map((err) => (
      <ErrorMessage key={`${err}`}>{err}</ErrorMessage>
    ))
  );
};

ErrorHandler.propTypes = {
  errorMessages: PropTypes.arrayOf(PropTypes.string).isRequired,
};

/*
 * Wrapper to make sure to display add story view or add note view
 */

const StoryGroupWrapper = ({
  onDeleteStory,
  onMonitorUpdateProgress,
  onUploadFinished,
  onDropCard,
  type,
}) => {
  const dispatch = useDispatch();
  const errorMessages = useSelector((state) => state.storyGroupComposer.errors);
  const { editingPostId } = useSelector((state) => state[type]);
  const editMode = !!editingPostId;
  const storyGroup = useSelector(
    (state) => state.storyGroupComposer.storyGroup
  );
  const cards = storyGroup ? storyGroup.stories : [];
  const [viewMode, setViewMode] = useState(ADD_STORY);
  const [story, setStory] = useState();

  const maxStories = useSelector(
    (state) => state.storyGroupComposer.maxStories
  );
  const notifiers = useStoryGroupNotifiers();
  const { oldUploadCallback } = useOldUploaderCallback({
    randomId: true,
    passedNotifiers: notifiers,
  });

  const onError = ({ message }) => {
    notifiers.queueError({ message });
  };

  const onFileReady = (file, source) => {
    if (!file) return;

    validateFile(file, {
      fileRestrictions: UploaderRestrictions.new({
        uploadConfig: FileUploadConfig.STORIES,
      }),
      // @REVIEW: do we need to pass currentFiles here?
      currentFiles: [],
    }).then((result) => {
      if (result instanceof File) {
        oldUploadCallback(
          'willberandomized',
          file,
          UploadTypes.MEDIA,
          undefined,
          source
        );
      } else {
        onError(result);
      }
    });
  };

  // call to the hook that returns services configuration for the context
  const { services, activeService } = useIntegrationServices({
    context: 'publishComposer',
    onFileReady,
    updateType: PostTypeStory, // only used for uploadCareOptions to make differences in case of being PostTypeStory
    serviceName: 'instagramStory',
    onError,
    enableCanva: true,
    enableGiphy: false,
  });

  const hasReachedMaxStories = maxStories - cards.length <= 0;
  const totalStoriesInCarousel = hasReachedMaxStories
    ? maxStories + 1
    : maxStories;
  const { cardWidth, cardHeight } = getCardSizes(true);

  const onCreateNewStoryCard = ({ id, uploaderInstance, file, source }) => {
    dispatch(
      actions.createNewStoryCard({ id, uploaderInstance, file, source })
    );
  };

  const saveNote = ({ note, order }) => {
    dispatch(actions.handleSaveStoryNote({ note, order }));
    dispatch(
      actions.trackNote({
        cta: SEGMENT_NAMES.STORIES_COMPOSER_ADD_NOTE,
        note,
        order,
      })
    );
  };

  return (
    <WrapperStyle>
      <HeaderBar />
      {viewMode === ADD_STORY && (
        <>
          <Carousel
            totalCardsToShow={totalStoriesInCarousel}
            largeCards
            totalStories={
              (storyGroup.stories && storyGroup.stories.length) || 0
            }
          >
            <CarouselCards
              createNewFile={onCreateNewStoryCard}
              monitorUpdateProgress={onMonitorUpdateProgress}
              cards={cards}
              totalCardsToShow={maxStories}
              largeCards
              editMode
              onDropCard={onDropCard}
              onAddNoteClick={(storyCard) => {
                setStory(storyCard);
                setViewMode(ADD_NOTE);
              }}
              onDeleteStoryClick={(storyCard) => onDeleteStory(storyCard)}
              onUploadFinished={(upload) =>
                onUploadFinished(upload, storyGroup)
              }
            />
            {hasReachedMaxStories && (
              <CarouselCard
                key="congrats"
                cardHeight={cardHeight}
                cardWidth={cardWidth}
                largeCards
                isTarget={false}
              >
                <CarouselCardCongrats weight={fontWeightMedium}>
                  You’re a Stories superstar!
                </CarouselCardCongrats>
                <CarouselCardCongrats weight={fontWeight}>
                  {`You’ve hit the ${maxStories} media limit per scheduled Story.`}
                </CarouselCardCongrats>
              </CarouselCard>
            )}
          </Carousel>
          <ErrorHandler errorMessages={errorMessages} />
          <IntegrationsWrapper>
            <IntegrationsBar
              services={services}
              activeService={activeService}
              disableBar={hasReachedMaxStories}
            />
          </IntegrationsWrapper>

          <AddStoryFooter
            storyGroup={storyGroup}
            editMode={editMode}
            type={type}
            isPastDue={storyGroup.isPastDue}
          />
        </>
      )}
      {viewMode === ADD_NOTE && (
        <AddNote
          onCancelClick={() => setViewMode(ADD_STORY)}
          story={story}
          onSaveNoteClick={({ order, note }) => {
            saveNote({ order, note });
            setViewMode(ADD_STORY);
          }}
        />
      )}
    </WrapperStyle>
  );
};

StoryGroupWrapper.propTypes = {
  type: PropTypes.string.isRequired,
  ...CardItem.propTypes,
};

export default StoryGroupWrapper;
